import React, { useEffect, useState } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { usePermissions } from "../../context/PermissionContext";

const QuotationList = () => {
  const [quotations, setQuotations] = useState([]);
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete, canExport } = usePermissions();

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      const res = await api.get("/quotations/");
      setQuotations(res.data);
    } catch (err) {
      console.error("Error fetching quotations:", err);
    }
  };

  const handleDownloadPDF = async (q) => {
    const doc = new jsPDF();
    const themeColor = [0, 128, 128]; // Teal

    // Add Logo
    try {
      const logoUrl = process.env.PUBLIC_URL + "/rekonic-logo.jpg";
      const logoImg = await new Promise((resolve) => {
        const img = new Image();
        img.src = logoUrl;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
      if (logoImg) {
        doc.addImage(logoImg, "JPEG", 15, 10, 50, 20);
      }
    } catch (e) {
      console.error("Logo failed to load", e);
    }

    doc.setFontSize(22);
    doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.text("QUOTATION", 195, 25, { align: "right" });

    doc.setDrawColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.setLineWidth(1);
    doc.line(15, 35, 195, 35);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, "bold");
    doc.text("Quotation Details:", 15, 45);
    doc.setFont(undefined, "normal");
    doc.text(`ID: ${q.id}`, 15, 52);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 15, 59);
    doc.text(`Status: ${q.status || "Draft"}`, 15, 66);

    doc.setFont(undefined, "bold");
    doc.text("Bill To:", 130, 45);
    doc.setFont(undefined, "normal");
    doc.text(`${q.customer_name}`, 130, 52);

    // Table
    const services = q.services || [];
    const tableRows = services.map((s) => [
      s.service_name || "-",
      s.quantity?.toString() || "0",
      Number(s.unit_price || 0).toFixed(2),
      Number(s.total_price || 0).toFixed(2),
    ]);

    autoTable(doc, {
      startY: 75,
      head: [["Service Name", "Qty", "Unit Price", "Total"]],
      body: tableRows,
      headStyles: { fillStyle: "F", fillColor: themeColor, textColor: 255 },
      alternateRowStyles: { fillColor: [240, 250, 250] },
      margin: { left: 15, right: 15 },
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    const subtotal = services.reduce((sum, s) => sum + (s.total_price || 0), 0);

    doc.setFont(undefined, "bold");
    doc.text(`Subtotal:`, 140, finalY);
    doc.setFont(undefined, "normal");
    doc.text(`${subtotal.toFixed(2)}`, 195, finalY, { align: "right" });

    let currentY = finalY + 7;
    doc.setFillColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.rect(135, currentY, 60, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text(`Grand Total:`, 140, currentY + 7);
    doc.text(`${Number(q.total_amount || subtotal).toFixed(2)}`, 190, currentY + 7, { align: "right" });

    // Stamp
    try {
      const stampUrl = process.env.PUBLIC_URL + "/stamp.jpg";
      const stampImg = await new Promise((resolve) => {
        const img = new Image();
        img.src = stampUrl;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
      if (stampImg) {
        doc.addImage(stampImg, "JPEG", 20, currentY + 20, 30, 30);
      }
    } catch (e) {
      console.error("Stamp failed to load", e);
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text("Authorized Signature", 20, currentY + 55);

    doc.save(`Quotation_${q.id}.pdf`);
  };

  const handleDeleteQuotation = async (id) => {
    if (!canDelete("quotations")) {
      alert("Access Denied: You do not have permission to delete quotations. Please contact your administrator.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this quotation?")) {
      try {
        await api.delete(`/quotations/${id}`);
        setQuotations(quotations.filter((q) => q.id !== id));
        alert("Quotation deleted successfully!");
      } catch (err) {
        console.error(err);
        alert("Failed to delete quotation.");
      }
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Quotations</h3>
        <button
          className={`btn ${canCreate("quotations") ? "btn-primary" : "btn-secondary opacity-50"}`}
          onClick={() => {
            if (!canCreate("quotations")) {
              alert("Access Denied: You do not have permission to create quotations.");
              return;
            }
            navigate("/dashboard/quotations/new");
          }}
          title={canCreate("quotations") ? "Create New Quotation" : "Permission Denied: Cannot create quotations"}
        >
          New Quotation
        </button>
      </div>

      <table className="table table-bordered table-hover">
        <thead className="table-light">
          <tr>
            <th>ID</th>
            <th>Customer</th>
            <th>Status</th>
            <th>Total</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {quotations.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center">
                No quotations found
              </td>
            </tr>
          ) : (
            quotations.map((q) => (
              <tr key={q.id}>
                <td>{q.id}</td>
                <td>{q.customer_name}</td>
                <td>{q.status}</td>
                <td>{q.total_amount ?? "-"}</td>
                <td>
                  <button
                    className={`btn btn-sm btn-warning me-2 ${!canEdit("quotations") ? "opacity-50" : ""}`}
                    onClick={() => {
                      if (!canEdit("quotations")) {
                        alert("Access Denied: You do not have permission to edit quotations.");
                        return;
                      }
                      navigate(`/dashboard/quotations/edit/${q.id}`);
                    }}
                    title={canEdit("quotations") ? "Edit Quotation" : "Permission Denied: Cannot edit"}
                  >
                    Edit
                  </button>
                  <button
                    className={`btn btn-sm btn-info me-2 ${!canExport("quotations") ? "opacity-50" : ""}`}
                    onClick={() => {
                      if (!canExport("quotations")) {
                        alert("Access Denied: You do not have permission to download/export quotations.");
                        return;
                      }
                      handleDownloadPDF(q);
                    }}
                    title={canExport("quotations") ? "Download PDF" : "Permission Denied: Cannot download PDF"}
                  >
                    📄 PDF
                  </button>
                  <button
                    className={`btn btn-sm btn-danger ${!canDelete("quotations") ? "opacity-40" : ""}`}
                    onClick={() => handleDeleteQuotation(q.id)}
                    title={canDelete("quotations") ? "Delete Quotation" : "Permission Denied: Cannot delete quotations"}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default QuotationList;
